import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Header from './components/Header';
import Hero from './components/Hero';
import Presentation from './components/Presentation';
import IndustryFocus from './components/IndustryFocus';
import DeploymentOptions from './components/DeploymentOptions';
import CallToAction from './components/CallToAction';
import Contact from './components/Contact';
import Footer from './components/Footer';
import DemoRequestModal from './components/DemoRequestModal';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Technologies from './components/Technologies';
import Partners from './components/Partners';
import CoreFeatures from './components/CoreFeatures';
import WorkflowAnimation from './components/WorkflowAnimation';
import VideoShowcase from './components/VideoShowcase';
import { DemoModalProvider, useDemoModal } from './hooks/useDemoModal';
import { DetailModalProvider } from './hooks/useDetailModal';
import SEO from './components/SEO';
import AlphanetixMesh from './components/AlphanetixMesh';
import LightOrbs from './components/LightOrbs';
import SplashScreen from './components/SplashScreen';
import CustomCursor from './components/CustomCursor';
import SectionNav from './components/SectionNav';
import { ThemeContextProvider } from './contexts/ThemeContext';
import CookieConsent from './components/CookieConsent';
import Insights from './pages/Insights';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Scroll progress bar — thin accent line at top of viewport
function ScrollProgressBar() {
  const [progress, setProgress] = useState(0);
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    const total = scrollHeight - clientHeight;
    setProgress(total > 0 ? (scrollTop / total) * 100 : 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (progress < 1) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: 3,
        width: `${progress}%`,
        zIndex: 10000,
        background: isLight
          ? 'linear-gradient(90deg, #0f172a, #334155, #64748b)'
          : 'linear-gradient(90deg, #10b981, #0ea5e9, #38bdf8)',
        transition: 'width 0.05s linear',
        borderRadius: '0 2px 2px 0',
        boxShadow: isLight
          ? '0 0 10px rgba(0, 0, 0, 0.12)'
          : '0 0 10px rgba(16, 185, 129, 0.5)',
      }}
    />
  );
}

// Home page with all sections
function HomePage() {
  return (
    <>
      <SEO 
        title="Alphanetix - Agentic AI Automation & Enterprise Integration Platform"
        description="Build agentic AI workflows visually and deploy them as compiled native code. Enterprise-proven iPaaS with 2,000+ connectors, GPT/Claude/Groq orchestration, visual logic, and on-premise, cloud, or hybrid deployment — across FinTech, Healthcare, Manufacturing, and more."
        keywords="agentic AI, AI workflow automation, AI agent orchestration, integration platform, enterprise automation, no-code workflow builder, iPaaS, data integration, API management, business process automation, hybrid deployment, on-premise integration, cloud integration, ETL, connector platform, workflow orchestration, real-time data sync, enterprise middleware, digital transformation, GPT integration, Claude integration, compiled workflows"
        ogImage="/images/company_logo_dark.jpg"
      />
      <Hero />
      <CoreFeatures />
      <WorkflowAnimation />
      <VideoShowcase />
      <Partners />
      <IndustryFocus />
      <Technologies />
      <DeploymentOptions />
      <CallToAction />
      <Contact />
    </>
  );
}

// Standalone presentation page — /presentation
function PresentationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoplay = searchParams.get('autoplay') === 'true';
  const slide = parseInt(searchParams.get('slide'), 10) || 0;

  const handleClose = useCallback(() => {
    navigate('/');
  }, [navigate]);

  return (
    <>
      <SEO
        title="Alphanetix Presentation - Enterprise Automation Platform"
        description="Interactive presentation showcasing Alphanetix's enterprise automation and integration platform capabilities."
        keywords="presentation, demo, enterprise automation, no-code platform, business integration"
        ogImage="/images/company_logo_dark.jpg"
      />
      <Presentation
        open={true}
        onClose={handleClose}
        initialSlide={slide}
        autoStart={autoplay}
      />
    </>
  );
}

// Wrap the content with the modal
function AppContent({ onReady }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const { isModalOpen, closeModal } = useDemoModal();

  // Signal that the app content has rendered
  useEffect(() => {
    onReady?.();
  }, [onReady]);

  return (
    <Box
      className={!isLight ? 'noise-texture' : undefined}
      sx={{
        minHeight: '100vh',
        position: 'relative',
        background: isLight
          ? '#FAFAF9'
          : '#0f172a',
      }}
    >
      {/* Alphanetix Mesh — signature brand network topology background */}
      <AlphanetixMesh />
      {/* Light-mode parallax gradient orbs */}
      <LightOrbs />
      {/* Custom branded cursor (desktop only) */}
      <CustomCursor />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
      <ScrollProgressBar />
      {/* Floating section dot-nav (desktop only) */}
      <SectionNav />
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      <Header />
      <Box id="main-content" component="main" sx={{ paddingTop: {  md: 0 } }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/presentation" element={<PresentationPage />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/privacy-policy" element={
            <>
              <SEO 
                title="Privacy Policy - Alphanetix"
                description="Privacy Policy for Alphanetix enterprise automation platform."
                keywords="privacy policy, data protection, enterprise security"
                ogImage="/images/company_logo_dark.jpg"
              />
              <PrivacyPolicy />
            </>
          } />
          <Route path="/terms-of-service" element={
            <>
              <SEO 
                title="Terms of Service - Alphanetix"
                description="Terms of Service for Alphanetix enterprise automation platform."
                keywords="terms of service, user agreement, enterprise platform"
                ogImage="/images/company_logo_dark.jpg"
              />
              <TermsOfService />
            </>
          } />
        </Routes>
      </Box>
      <Footer />
      <CookieConsent />
      <DemoRequestModal open={isModalOpen} handleClose={closeModal} />
      </Box>
    </Box>
  );
}

function App() {
  const [splashDone, setSplashDone] = useState(false);
  const [appReady, setAppReady] = useState(false);

  const handleSplashFinished = useCallback(() => {
    setSplashDone(true);
  }, []);

  // Signal that the app content has mounted and rendered
  const handleAppReady = useCallback(() => {
    setAppReady(true);
  }, []);

  return (
    <BrowserRouter>
      <ThemeContextProvider>
        {!splashDone && <SplashScreen onFinished={handleSplashFinished} appReady={appReady} />}
        <DemoModalProvider>
          <DetailModalProvider>
            <ScrollToTop />
            <AppContent onReady={handleAppReady} />
          </DetailModalProvider>
        </DemoModalProvider>
      </ThemeContextProvider>
    </BrowserRouter>
  );
}

export default App;